import { endAndReportPerformanceMeasure, startPerformanceMeasure } from '@ms/yammer-telemetry';
import type { PerformanceEventProperties, PerformanceMeasure } from '@ms/yammer-telemetry-support';

import type { ScenarioId, ScenarioName, ScenarioSource, ScenarioStatus } from './types';

const fiveMinutesInMS = 300_000;

interface ScenarioHandle {
  readonly measure: PerformanceMeasure;
  readonly name: ScenarioName;
  readonly source?: ScenarioSource;
  readonly timer: number;
}

const scenarios = new Map<ScenarioId, ScenarioHandle>();

interface ReliabilityFlags {
  readonly endedWithError: boolean;
  readonly timedOut: boolean;
  readonly interrupted: boolean;
}

/**
 * Maps the richer scenario status onto the primitive's boolean reliability flags so today's
 * reliability metric keeps working. The full status value always rides along in `scenarioStatus`.
 */
const statusToReliabilityFlags: Record<ScenarioStatus, ReliabilityFlags> = {
  ok: { endedWithError: false, timedOut: false, interrupted: false },
  failure: { endedWithError: true, timedOut: false, interrupted: false },
  timeout: { endedWithError: false, timedOut: true, interrupted: false },
  cancel: { endedWithError: false, timedOut: false, interrupted: true },
  incomplete: { endedWithError: false, timedOut: false, interrupted: true },
  ignore: { endedWithError: false, timedOut: false, interrupted: false },
};

export interface LogScenarioStopOptions {
  readonly scenarioId: ScenarioId;
  readonly status: ScenarioStatus;
  readonly error?: Error;
}

/**
 * Ends a scenario and reports it. `status` drives the reliability flags and is attached as a
 * property alongside the scenario name, source and scenario id. A `failure` status may
 * carry an `error` whose name/message become `errorCode`/`errorMessage`. Unknown or
 * already-stopped scenario ids are a no-op.
 */
export const logScenarioStop = ({ scenarioId, status, error }: LogScenarioStopOptions): void => {
  const handle = scenarios.get(scenarioId);

  if (!handle) {
    return;
  }

  window.clearTimeout(handle.timer);
  scenarios.delete(scenarioId);

  const { measure, name, source } = handle;

  const eventProperties: PerformanceEventProperties = {
    ...measure.eventProperties,
    ...statusToReliabilityFlags[status],
    scenario: name,
    scenarioStatus: status,
    scenarioId,
    ...(source ? { source } : {}),
    ...(error ? { errorCode: error.name, errorMessage: error.message } : {}),
  };

  endAndReportPerformanceMeasure({ ...measure, eventProperties });
};

export interface LogScenarioStartOptions {
  readonly name: ScenarioName;
  readonly source?: ScenarioSource;
  /**
   * How long to wait before the scenario auto-reports with a `timeout` status.
   * @default 300_000 (5 minutes)
   */
  readonly timeoutInMS?: number;
}

/**
 * Starts a scenario measure and returns an opaque scenario id. No event is emitted on
 * start (mirrors mobile). If `logScenarioStop` is not called within `timeoutInMS`, the
 * scenario auto-reports with a `timeout` status so orphaned scenarios never leak.
 */
export const logScenarioStart = ({ name, source, timeoutInMS }: LogScenarioStartOptions): ScenarioId => {
  const scenarioId = crypto.randomUUID() as ScenarioId;
  const measure = startPerformanceMeasure('multi_step_scenario', name);

  const timer = window.setTimeout(() => {
    logScenarioStop({ scenarioId, status: 'timeout' });
  }, timeoutInMS ?? fiveMinutesInMS);

  scenarios.set(scenarioId, { measure, name, source, timer });

  return scenarioId;
};
